import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listAccessRequests, listUsers, adminCreateUser,
  adminDeleteUser, updateAccessRequest,
} from "@/lib/auth/auth.functions";
import {
  createInvitation, listInvitations, revokeInvitation,
} from "@/lib/auth/invitations.functions";
import {
  listEmailSubscribers, deleteEmailSubscriber,
} from "@/lib/marketing/subscribers.functions";
import {
  adminListAllScans, adminListProfiles, adminListPrivateSources, adminGetStats,
  adminListSubscriptions, adminSubscriptionStats, adminGrantPlan, adminRevokePlan,
} from "@/lib/admin/overview.functions";
import { TIERS, TIER_ORDER, type TierSlug } from "@/lib/payments/tiers";
import { AppLayout } from "@/components/intel/Layout";
import { RequireAuth } from "@/lib/auth/RequireAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Shield, UserPlus, Trash2, Check, X, Inbox, Mail, Download, Activity, Database, Users, CreditCard, DollarSign, Sparkles } from "lucide-react";
import { MailPlus, Copy, Link2 } from "lucide-react";
import { OperationsDashboard } from "@/components/admin/OperationsDashboard";

export const Route = createFileRoute("/admin")({
  head: () => ({ meta: [
    { title: "Operations Control Room · CARIO INTEL" },
    { name: "description", content: "Admin operations, access, subscriptions, scans, and production telemetry for CARIO INTEL." },
    { property: "og:title", content: "Operations Control Room · CARIO INTEL" },
    { property: "og:description", content: "Secure administration and production observability for CARIO INTEL." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: () => (
    <RequireAuth adminOnly>
      <AppLayout><AdminPage /></AppLayout>
    </RequireAuth>
  ),
});

function AdminPage() {
  return (
    <div className="mx-auto max-w-7xl p-6 space-y-8">
      <Toaster />
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">// Administration</p>
        <h1 className="mt-1 font-display text-3xl flex items-center gap-2">
          <Shield className="h-6 w-6 text-cyan" /> Control room
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Manage operators and vet incoming access requests.</p>
      </header>

      <CreateUserCard />
      <StatsPanel />
      <OperationsDashboard />
      <UsersTable />
      <AccessRequestsTable />
      <SubscriptionsPanel />
      <InvitationsPanel />
      <SubscribersPanel />
      <AllScansPanel />
      <PrivateSourcesPanel />
      <ProfilesPanel />
    </div>
  );
}

function CreateUserCard() {
  const qc = useQueryClient();
  const createFn = useServerFn(adminCreateUser);
  const [form, setForm] = useState({ email: "", password: "", display_name: "", role: "analyst" as "admin" | "analyst" });
  const m = useMutation({
    mutationFn: async () => createFn({ data: form }),
    onSuccess: () => {
      toast.success("User created");
      setForm({ email: "", password: "", display_name: "", role: "analyst" });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  return (
    <section className="glass rounded-xl p-6">
      <h2 className="font-display text-lg flex items-center gap-2"><UserPlus className="h-4 w-4 text-cyan" /> Create new operator</h2>
      <form onSubmit={(e) => { e.preventDefault(); m.mutate(); }} className="mt-4 grid gap-4 md:grid-cols-5">
        <div className="space-y-1.5 md:col-span-2">
          <Label>Email</Label>
          <Input type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Password</Label>
          <Input type="text" required minLength={8} value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Display name</Label>
          <Input value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v as any })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="analyst">Analyst</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-5">
          <Button type="submit" disabled={m.isPending}>{m.isPending ? "Creating…" : "Create user"}</Button>
        </div>
      </form>
    </section>
  );
}

function UsersTable() {
  const list = useServerFn(listUsers);
  const del = useServerFn(adminDeleteUser);
  const listSubs = useServerFn(adminListSubscriptions);
  const update = useServerFn(updateAccessRequest);
  const qc = useQueryClient();
  const [lastInviteLink, setLastInviteLink] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => list(),
  });
  const { data: subs } = useQuery({
    queryKey: ["admin", "subscriptions"],
    queryFn: () => listSubs(),
  });
  // Map userId -> newest active subscription
  const now = Date.now();
  const activeByUser = new Map<string, any>();
  for (const s of (subs ?? [])) {
    const cpe = s.current_period_end ? new Date(s.current_period_end).getTime() : Infinity;
    const active =
      (["active", "trialing", "past_due"].includes(s.status) && cpe > now) ||
      (s.status === "canceled" && cpe > now);
    if (active && !activeByUser.has(s.user_id)) activeByUser.set(s.user_id, s);
  }
  const deleteUser = useMutation({
    mutationFn: (userId: string) => del({ data: { userId } }),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin", "users"] }); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });
  const activation = useMutation({
    mutationFn: (requestId: string) => update({ data: { id: requestId, status: "approved" } }),
    onSuccess: (res: any) => {
      if (res?.invitationToken) {
        const url = `${window.location.origin}/accept-invite?token=${encodeURIComponent(res.invitationToken)}`;
        setLastInviteLink(url);
        toast.success("Activation link created");
      } else {
        toast.success("Activation refreshed");
      }
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "access-requests"] });
      qc.invalidateQueries({ queryKey: ["admin", "invitations"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const statusRank: Record<string, number> = {
    registered: 0,
    approved_awaiting_activation: 1,
    pending_request: 2,
    rejected_request: 3,
  };
  const people = [...(data ?? [])].sort((a: any, b: any) => {
    const rank = (statusRank[a.account_status] ?? 9) - (statusRank[b.account_status] ?? 9);
    if (rank !== 0) return rank;
    return new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime();
  });
  const counts = people.reduce((acc: Record<string, number>, person: any) => {
    const key = person.account_status ?? "registered";
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});

  function statusBadge(status: string) {
    if (status === "registered") return <Badge variant="default">Registered</Badge>;
    if (status === "approved_awaiting_activation") return <Badge variant="default">Approved · awaiting activation</Badge>;
    if (status === "pending_request") return <Badge variant="secondary">Pending request</Badge>;
    return <Badge variant="destructive">Rejected request</Badge>;
  }

  return (
    <section className="glass rounded-xl p-6">
      <h2 className="font-display text-lg flex items-center gap-2">
        <Users className="h-4 w-4 text-cyan" /> People & access
        <Badge variant="secondary" className="ml-2">{data?.length ?? 0}</Badge>
      </h2>
      <p className="text-xs text-muted-foreground mt-1">All registered accounts, pending requests, approved requests awaiting activation, and rejected requests in one place.</p>
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <Badge variant="default">Registered {counts.registered ?? 0}</Badge>
        <Badge variant="default">Approved {counts.approved_awaiting_activation ?? 0}</Badge>
        <Badge variant="secondary">Pending {counts.pending_request ?? 0}</Badge>
        <Badge variant="destructive">Rejected {counts.rejected_request ?? 0}</Badge>
      </div>
      {lastInviteLink && (
        <div className="mt-4 rounded-md border border-cyan/40 bg-cyan/5 p-3 text-xs">
          <div className="flex items-center gap-2 font-mono break-all">
            <Link2 className="h-3.5 w-3.5 text-cyan flex-shrink-0" />
            <span className="flex-1">{lastInviteLink}</span>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(lastInviteLink); toast.success("Copied"); }}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
          <p className="mt-2 text-muted-foreground">Send this activation link to the approved user.</p>
        </div>
      )}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-b border-panel-border">
              <th className="py-2 pr-4">User</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4">Access request</th>
              <th className="py-2 pr-4">Roles</th>
              <th className="py-2 pr-4">Plan</th>
              <th className="py-2 pr-4">Created</th>
              <th className="py-2 pr-4">Last sign-in</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="py-4 text-muted-foreground">Loading…</td></tr>}
            {!isLoading && (data?.length ?? 0) === 0 && (
              <tr><td colSpan={8} className="py-4 text-muted-foreground text-xs">No people or requests yet.</td></tr>
            )}
            {people.map((u: any) => {
              const userId = u.auth_user_id ?? u.id;
              const sub = u.auth_user_id ? activeByUser.get(u.auth_user_id) : null;
              const tier = sub ? (TIERS as any)[sub.product_id] : null;
              return (
              <tr key={u.person_key ?? userId} className="border-b border-panel-border/50">
                <td className="py-2 pr-4">
                  <div className="font-mono text-xs break-all">{u.email}</div>
                  <div className="text-[10px] text-muted-foreground">{u.display_name || "No display name"}</div>
                </td>
                <td className="py-2 pr-4">
                  {statusBadge(u.account_status ?? "registered")}
                </td>
                <td className="py-2 pr-4">
                  {u.access_request ? (
                    <div className="flex flex-col gap-0.5">
                      <Badge variant={u.access_request.status === "approved" ? "default" : u.access_request.status === "rejected" ? "destructive" : "secondary"} className="w-fit">
                        {u.access_request.status}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {u.access_request.full_name}{u.access_request.organization ? ` · ${u.access_request.organization}` : ""}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">Direct registration</span>
                  )}
                </td>
                <td className="py-2 pr-4">
                  {u.roles.length ? u.roles.map((r: string) => (
                    <Badge key={r} variant={r === "admin" ? "default" : "secondary"} className="mr-1">{r}</Badge>
                  )) : <span className="text-xs text-muted-foreground">—</span>}
                </td>
                <td className="py-2 pr-4">
                  {!u.auth_user_id ? (
                    <span className="text-xs text-muted-foreground">Approve/activate first</span>
                  ) : sub ? (
                    <div className="flex flex-col gap-0.5">
                      <Badge variant="default" className="w-fit">{tier?.name ?? sub.product_id}</Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {sub.status}{sub.current_period_end ? ` · ends ${new Date(sub.current_period_end).toLocaleDateString()}` : " · no expiry"}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-2 pr-4 text-muted-foreground text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="py-2 pr-4 text-muted-foreground text-xs">{u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString() : "Never"}</td>
                <td className="py-2 text-right">
                  {u.auth_user_id ? (
                    <>
                      <GrantPlanDialog userId={u.auth_user_id} userEmail={u.email ?? ""} />
                      <Button size="sm" variant="ghost" onClick={() => { if (confirm(`Delete ${u.email}?`)) deleteUser.mutate(u.auth_user_id); }}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  ) : u.access_request?.status === "approved" ? (
                    <Button size="sm" variant="outline" onClick={() => activation.mutate(u.access_request.id)} disabled={activation.isPending}>
                      <Link2 className="h-3.5 w-3.5 mr-1" />Activation link
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function GrantPlanDialog({ userId, userEmail }: { userId: string; userEmail: string }) {
  const qc = useQueryClient();
  const grantFn = useServerFn(adminGrantPlan);
  const [open, setOpen] = useState(false);
  const [tier, setTier] = useState<TierSlug>("analyst_plan");
  const [days, setDays] = useState<number>(30);
  const [environment, setEnvironment] = useState<"live" | "sandbox">("live");
  const m = useMutation({
    mutationFn: async () => grantFn({ data: { userId, tier, days, environment } }),
    onSuccess: () => {
      toast.success(`Granted ${TIERS[tier].name} to ${userEmail}`);
      qc.invalidateQueries({ queryKey: ["admin", "subscriptions"] });
      qc.invalidateQueries({ queryKey: ["admin", "sub-stats"] });
      setOpen(false);
    },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="mr-2">
          <Sparkles className="h-3.5 w-3.5 mr-1" /> Grant
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant plan · {userEmail}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Plan</Label>
            <Select value={tier} onValueChange={(v) => setTier(v as TierSlug)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIER_ORDER.map((slug) => (
                  <SelectItem key={slug} value={slug}>
                    {TIERS[slug].name} · ${TIERS[slug].monthlyUsd}/mo
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Duration</Label>
            <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="365">1 year</SelectItem>
                <SelectItem value="0">No expiry</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Environment</Label>
            <Select value={environment} onValueChange={(v) => setEnvironment(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="live">Live (production)</SelectItem>
                <SelectItem value="sandbox">Test (preview)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={() => m.mutate()} disabled={m.isPending}>
            {m.isPending ? "Granting…" : "Grant plan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubscriptionsPanel() {
  const listFn = useServerFn(adminListSubscriptions);
  const revokeFn = useServerFn(adminRevokePlan);
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"all" | "active" | "canceled" | "manual">("all");
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "subscriptions"],
    queryFn: () => listFn(),
  });
  const revoke = useMutation({
    mutationFn: (subscriptionId: string) => revokeFn({ data: { subscriptionId } }),
    onSuccess: () => {
      toast.success("Revoked");
      qc.invalidateQueries({ queryKey: ["admin", "subscriptions"] });
      qc.invalidateQueries({ queryKey: ["admin", "sub-stats"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });
  const now = Date.now();
  const rows = (data ?? []).filter((s: any) => {
    if (filter === "manual") return s.source === "manual";
    if (filter === "canceled") return s.status === "canceled";
    if (filter === "active") {
      const cpe = s.current_period_end ? new Date(s.current_period_end).getTime() : Infinity;
      return (["active", "trialing", "past_due"].includes(s.status) && cpe > now) ||
             (s.status === "canceled" && cpe > now);
    }
    return true;
  });
  return (
    <section className="glass rounded-xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-cyan" /> Subscriptions
          <Badge variant="secondary" className="ml-2">{rows.length}</Badge>
        </h2>
        <Select value={filter} onValueChange={(v) => setFilter(v as any)}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
            <SelectItem value="manual">Manual grants</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs text-muted-foreground mt-1">Every subscription across live and test environments.</p>
      <div className="mt-4 overflow-x-auto max-h-[520px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-background/95 backdrop-blur">
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-b border-panel-border">
              <th className="py-2 pr-4">User</th>
              <th className="py-2 pr-4">Plan</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4">Source</th>
              <th className="py-2 pr-4">Env</th>
              <th className="py-2 pr-4">Period end</th>
              <th className="py-2 pr-4">Created</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="py-4 text-muted-foreground">Loading…</td></tr>}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={8} className="py-4 text-muted-foreground text-xs">No subscriptions match this filter.</td></tr>
            )}
            {rows.map((s: any) => {
              const tier = (TIERS as any)[s.product_id];
              const cpe = s.current_period_end ? new Date(s.current_period_end).getTime() : Infinity;
              const isActive = (["active", "trialing", "past_due"].includes(s.status) && cpe > now) ||
                               (s.status === "canceled" && cpe > now);
              return (
                <tr key={s.id} className="border-b border-panel-border/50">
                  <td className="py-2 pr-4 font-mono text-xs break-all">{s.user_email ?? s.user_id}</td>
                  <td className="py-2 pr-4 text-xs">{tier?.name ?? s.product_id}</td>
                  <td className="py-2 pr-4">
                    <Badge variant={isActive ? "default" : "secondary"}>{s.status}</Badge>
                  </td>
                  <td className="py-2 pr-4">
                    <Badge variant={s.source === "manual" ? "outline" : "secondary"}>{s.source}</Badge>
                  </td>
                  <td className="py-2 pr-4 text-xs text-muted-foreground">{s.environment}</td>
                  <td className="py-2 pr-4 text-xs text-muted-foreground">
                    {s.current_period_end ? new Date(s.current_period_end).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-2 pr-4 text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</td>
                  <td className="py-2 text-right">
                    {isActive && (
                      <Button size="sm" variant="ghost" onClick={() => {
                        if (confirm(`Revoke ${tier?.name ?? s.product_id} for ${s.user_email ?? s.user_id}?`)) revoke.mutate(s.id);
                      }}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StatsPanel() {
  const fn = useServerFn(adminGetStats);
  const subFn = useServerFn(adminSubscriptionStats);
  const { data, isLoading } = useQuery({ queryKey: ["admin", "stats"], queryFn: () => fn() });
  const { data: subStats } = useQuery({ queryKey: ["admin", "sub-stats"], queryFn: () => subFn() });
  const items = [
    { label: "People", value: data?.users, icon: Users },
    { label: "Scans run", value: data?.scans, icon: Activity },
    { label: "Email subscribers", value: data?.subscribers, icon: Mail },
    { label: "Private sources", value: data?.privateSources, icon: Database },
    { label: "Open access", value: data?.pendingAccess, icon: Inbox },
    {
      label: "Active subs (live)",
      value: subStats?.live.active,
      icon: CreditCard,
    },
    {
      label: "MRR (live)",
      value: subStats ? `$${subStats.live.mrr.toLocaleString()}` : undefined,
      icon: DollarSign,
    },
    {
      label: "Active subs (test)",
      value: subStats?.sandbox.active,
      icon: CreditCard,
    },
  ];
  return (
    <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {items.map((it) => (
        <div key={it.label} className="glass rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{it.label}</p>
            <it.icon className="h-4 w-4 text-cyan" />
          </div>
          <p className="mt-2 font-display text-2xl">{isLoading ? "…" : (it.value ?? 0)}</p>
        </div>
      ))}
    </section>
  );
}

function AllScansPanel() {
  const fn = useServerFn(adminListAllScans);
  const { data, isLoading } = useQuery({ queryKey: ["admin", "all-scans"], queryFn: () => fn() });
  return (
    <section className="glass rounded-xl p-6">
      <h2 className="font-display text-lg flex items-center gap-2">
        <Activity className="h-4 w-4 text-cyan" /> All scans
        <Badge variant="secondary" className="ml-2">{data?.length ?? 0}</Badge>
      </h2>
      <p className="text-xs text-muted-foreground mt-1">Every intel scan executed on the platform, including who ran it when ownership is available.</p>
      <div className="mt-4 overflow-x-auto max-h-[480px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-background/95 backdrop-blur">
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-b border-panel-border">
              <th className="py-2 pr-4">User</th>
              <th className="py-2 pr-4">Target</th>
              <th className="py-2 pr-4">Type</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4">Risk</th>
              <th className="py-2 pr-4">Sources</th>
              <th className="py-2 pr-4">Started</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="py-4 text-muted-foreground">Loading…</td></tr>}
            {!isLoading && (data?.length ?? 0) === 0 && (
              <tr><td colSpan={7} className="py-4 text-muted-foreground text-xs">No scans yet.</td></tr>
            )}
            {data?.map((s: any) => (
              <tr key={s.id} className="border-b border-panel-border/50">
                <td className="py-2 pr-4">
                  {s.user_id ? (
                    <div className="flex flex-col gap-0.5">
                      <span className="font-mono text-xs break-all">{s.user_email ?? s.user_id}</span>
                      <span className="text-[10px] text-muted-foreground">{s.user_display_name ?? s.user_id}</span>
                    </div>
                  ) : (
                    <Badge variant="outline">Legacy / unassigned</Badge>
                  )}
                </td>
                <td className="py-2 pr-4 font-mono text-xs break-all">{s.target}</td>
                <td className="py-2 pr-4 text-xs text-muted-foreground">{s.target_type}</td>
                <td className="py-2 pr-4">
                  <Badge variant={s.status === "completed" ? "default" : s.status === "failed" ? "destructive" : "secondary"}>
                    {s.status}
                  </Badge>
                </td>
                <td className="py-2 pr-4 text-xs">{s.risk_score}</td>
                <td className="py-2 pr-4 text-xs text-muted-foreground">{s.source_count}</td>
                <td className="py-2 pr-4 text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PrivateSourcesPanel() {
  const fn = useServerFn(adminListPrivateSources);
  const { data, isLoading } = useQuery({ queryKey: ["admin", "private-sources"], queryFn: () => fn() });
  return (
    <section className="glass rounded-xl p-6">
      <h2 className="font-display text-lg flex items-center gap-2">
        <Database className="h-4 w-4 text-cyan" /> Private sources
        <Badge variant="secondary" className="ml-2">{data?.length ?? 0}</Badge>
      </h2>
      <p className="text-xs text-muted-foreground mt-1">Custom data sources registered by operators.</p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-b border-panel-border">
              <th className="py-2 pr-4">Name</th>
              <th className="py-2 pr-4">Kind</th>
              <th className="py-2 pr-4">Owner</th>
              <th className="py-2 pr-4">Enabled</th>
              <th className="py-2 pr-4">Created</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} className="py-4 text-muted-foreground">Loading…</td></tr>}
            {!isLoading && (data?.length ?? 0) === 0 && (
              <tr><td colSpan={5} className="py-4 text-muted-foreground text-xs">No private sources.</td></tr>
            )}
            {data?.map((s: any) => (
              <tr key={s.id} className="border-b border-panel-border/50">
                <td className="py-2 pr-4 text-xs">{s.name}</td>
                <td className="py-2 pr-4"><Badge variant="secondary">{s.kind}</Badge></td>
                <td className="py-2 pr-4 font-mono text-[10px] text-muted-foreground">{s.owner_user_id}</td>
                <td className="py-2 pr-4 text-xs">{s.enabled ? "yes" : "no"}</td>
                <td className="py-2 pr-4 text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ProfilesPanel() {
  const fn = useServerFn(adminListProfiles);
  const { data, isLoading } = useQuery({ queryKey: ["admin", "profiles"], queryFn: () => fn() });
  return (
    <section className="glass rounded-xl p-6">
      <h2 className="font-display text-lg flex items-center gap-2">
        <Users className="h-4 w-4 text-cyan" /> User directory
        <Badge variant="secondary" className="ml-2">{data?.length ?? 0}</Badge>
      </h2>
      <p className="text-xs text-muted-foreground mt-1">Every registered profile.</p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-b border-panel-border">
              <th className="py-2 pr-4">Email</th>
              <th className="py-2 pr-4">Display name</th>
              <th className="py-2 pr-4">User ID</th>
              <th className="py-2 pr-4">Joined</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={4} className="py-4 text-muted-foreground">Loading…</td></tr>}
            {data?.map((p: any) => (
              <tr key={p.user_id} className="border-b border-panel-border/50">
                <td className="py-2 pr-4 font-mono text-xs">{p.email}</td>
                <td className="py-2 pr-4 text-xs">{p.display_name || "—"}</td>
                <td className="py-2 pr-4 font-mono text-[10px] text-muted-foreground">{p.user_id}</td>
                <td className="py-2 pr-4 text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AccessRequestsTable() {
  const list = useServerFn(listAccessRequests);
  const listRegisteredUsers = useServerFn(listUsers);
  const update = useServerFn(updateAccessRequest);
  const qc = useQueryClient();
  const [lastInviteLink, setLastInviteLink] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "access-requests"],
    queryFn: () => list(),
  });
  const { data: registeredUsers } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => listRegisteredUsers(),
  });
  const userByEmail = new Map(
    (registeredUsers ?? [])
      .filter((u: any) => !!u.auth_user_id)
      .map((u: any) => [(u.email ?? "").toLowerCase(), u]),
  );
  const m = useMutation({
    mutationFn: (vars: { id: string; status: "approved" | "rejected" }) => update({ data: vars }),
    onSuccess: (res: any) => {
      if (res?.invitationToken) {
        const url = `${window.location.origin}/accept-invite?token=${encodeURIComponent(res.invitationToken)}`;
        setLastInviteLink(url);
        toast.success("Approved — registration link created");
      } else if (res?.invitationAlreadyExists) {
        toast.success("Approved — an active registration link already exists");
      } else {
        toast.success("Updated");
      }
      qc.invalidateQueries({ queryKey: ["admin", "access-requests"] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "invitations"] });
      qc.invalidateQueries({ queryKey: ["admin", "stats"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  return (
    <section className="glass rounded-xl p-6">
      <h2 className="font-display text-lg flex items-center gap-2">
        <Inbox className="h-4 w-4 text-cyan" /> Access requests
        <Badge variant="secondary" className="ml-2">{data?.length ?? 0}</Badge>
      </h2>
      <p className="text-xs text-muted-foreground mt-1">Approve requests to generate an activation link. Approved and registered people are also shown in People & access.</p>

      {lastInviteLink && (
        <div className="mt-4 rounded-md border border-cyan/40 bg-cyan/5 p-3 text-xs">
          <div className="flex items-center gap-2 font-mono break-all">
            <Link2 className="h-3.5 w-3.5 text-cyan flex-shrink-0" />
            <span className="flex-1">{lastInviteLink}</span>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(lastInviteLink); toast.success("Copied"); }}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
          <p className="mt-2 text-muted-foreground">Send this link to the approved user. It is shown once.</p>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {isLoading && <div className="text-muted-foreground text-sm">Loading…</div>}
        {!isLoading && (data?.length ?? 0) === 0 && (
          <div className="text-muted-foreground text-sm">No access requests yet.</div>
        )}
        {data?.map((r: any) => {
          const linkedUser = userByEmail.get((r.email ?? "").toLowerCase()) as any | undefined;
          return (
          <div key={r.id} className="rounded-md border border-panel-border bg-background/40 p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="font-display text-base">{r.full_name} <span className="text-muted-foreground text-xs font-mono ml-2">{r.email}</span></div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {r.organization || "—"} · {new Date(r.created_at).toLocaleString()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={r.status === "approved" ? "default" : r.status === "rejected" ? "destructive" : "secondary"}>
                  {r.status}
                </Badge>
                {r.status === "pending" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => m.mutate({ id: r.id, status: "approved" })}>
                      <Check className="h-3.5 w-3.5 mr-1" />Approve
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => m.mutate({ id: r.id, status: "rejected" })}>
                      <X className="h-3.5 w-3.5 mr-1" />Reject
                    </Button>
                  </>
                )}
                {r.status === "approved" && linkedUser && (
                  <GrantPlanDialog userId={linkedUser.auth_user_id} userEmail={linkedUser.email ?? r.email} />
                )}
                {r.status === "approved" && !linkedUser && (
                  <>
                    <Badge variant="outline">Awaiting activation</Badge>
                    <Button size="sm" variant="outline" onClick={() => m.mutate({ id: r.id, status: "approved" })}>
                      <Link2 className="h-3.5 w-3.5 mr-1" />Activation link
                    </Button>
                  </>
                )}
              </div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap">{r.reason}</p>
          </div>
          );
        })}
      </div>
    </section>
  );
}

function InvitationsPanel() {
  const qc = useQueryClient();
  const createFn = useServerFn(createInvitation);
  const listFn = useServerFn(listInvitations);
  const revokeFn = useServerFn(revokeInvitation);
  const [form, setForm] = useState({ email: "", role: "analyst" as "admin" | "analyst" | "authority", days: 14 });
  const [lastLink, setLastLink] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "invitations"],
    queryFn: () => listFn(),
  });

  const create = useMutation({
    mutationFn: async () => createFn({ data: form }),
    onSuccess: (res: any) => {
      const url = `${window.location.origin}/accept-invite?token=${encodeURIComponent(res.token)}`;
      setLastLink(url);
      toast.success("Invitation created — copy the link to share");
      setForm({ email: "", role: "analyst", days: 14 });
      qc.invalidateQueries({ queryKey: ["admin", "invitations"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeFn({ data: { id } }),
    onSuccess: () => { toast.success("Revoked"); qc.invalidateQueries({ queryKey: ["admin", "invitations"] }); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  return (
    <section className="glass rounded-xl p-6">
      <h2 className="font-display text-lg flex items-center gap-2"><MailPlus className="h-4 w-4 text-cyan" /> Invitations</h2>
      <p className="text-xs text-muted-foreground mt-1">Registration is invite-only. Generate a one-time link and share it with the operator.</p>

      <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }} className="mt-4 grid gap-4 md:grid-cols-5">
        <div className="space-y-1.5 md:col-span-2">
          <Label>Email</Label>
          <Input type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v as any })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="analyst">Analyst</SelectItem>
              <SelectItem value="authority">Authority</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Expires (days)</Label>
          <Input type="number" min={1} max={60} value={form.days}
            onChange={(e) => setForm({ ...form, days: parseInt(e.target.value || "14", 10) })} />
        </div>
        <div className="flex items-end">
          <Button type="submit" disabled={create.isPending} className="w-full">
            {create.isPending ? "Creating…" : "Generate link"}
          </Button>
        </div>
      </form>

      {lastLink && (
        <div className="mt-4 rounded-md border border-cyan/40 bg-cyan/5 p-3 text-xs">
          <div className="flex items-center gap-2 font-mono break-all">
            <Link2 className="h-3.5 w-3.5 text-cyan flex-shrink-0" />
            <span className="flex-1">{lastLink}</span>
            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(lastLink); toast.success("Copied"); }}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
          <p className="mt-2 text-muted-foreground">This link is shown once. Save it now.</p>
        </div>
      )}

      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-b border-panel-border">
              <th className="py-2 pr-4">Email</th>
              <th className="py-2 pr-4">Role</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4">Expires</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} className="py-4 text-muted-foreground">Loading…</td></tr>}
            {data?.map((inv: any) => {
              const used = !!inv.used_at;
              const expired = !used && new Date(inv.expires_at) < new Date();
              return (
                <tr key={inv.id} className="border-b border-panel-border/50">
                  <td className="py-2 pr-4 font-mono text-xs">{inv.email}</td>
                  <td className="py-2 pr-4"><Badge variant="secondary">{inv.role}</Badge></td>
                  <td className="py-2 pr-4">
                    <Badge variant={used ? "default" : expired ? "destructive" : "secondary"}>
                      {used ? "used" : expired ? "expired" : "pending"}
                    </Badge>
                  </td>
                  <td className="py-2 pr-4 text-xs text-muted-foreground">{new Date(inv.expires_at).toLocaleDateString()}</td>
                  <td className="py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => { if (confirm(`Revoke invite for ${inv.email}?`)) revoke.mutate(inv.id); }}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              );
            })}
            {!isLoading && (data?.length ?? 0) === 0 && (
              <tr><td colSpan={5} className="py-4 text-muted-foreground text-xs">No invitations yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
function SubscribersPanel() {
  const listFn = useServerFn(listEmailSubscribers);
  const delFn = useServerFn(deleteEmailSubscriber);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "subscribers"],
    queryFn: () => listFn(),
  });
  const m = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["admin", "subscribers"] }); },
    onError: (e: any) => toast.error(e?.message || "Failed"),
  });

  function exportCsv() {
    const rows = data ?? [];
    const header = "email,source,created_at\n";
    const body = rows.map((r: any) =>
      `${r.email},${(r.source ?? "").replace(/,/g, " ")},${r.created_at}`
    ).join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `cario-subscribers-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  return (
    <section className="glass rounded-xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg flex items-center gap-2">
          <Mail className="h-4 w-4 text-cyan" /> Email list
          <Badge variant="secondary" className="ml-2">{data?.length ?? 0}</Badge>
        </h2>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={!data?.length}>
          <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Every email captured from the landing page, hero, footer, and software request form.
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-b border-panel-border">
              <th className="py-2 pr-4">Email</th>
              <th className="py-2 pr-4">Source</th>
              <th className="py-2 pr-4">Captured</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={4} className="py-4 text-muted-foreground">Loading…</td></tr>}
            {!isLoading && (data?.length ?? 0) === 0 && (
              <tr><td colSpan={4} className="py-4 text-muted-foreground text-xs">No signups yet.</td></tr>
            )}
            {data?.map((s: any) => (
              <tr key={s.id} className="border-b border-panel-border/50">
                <td className="py-2 pr-4 font-mono text-xs">{s.email}</td>
                <td className="py-2 pr-4 text-xs text-muted-foreground">{s.source || "—"}</td>
                <td className="py-2 pr-4 text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString()}</td>
                <td className="py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm(`Remove ${s.email}?`)) m.mutate(s.id); }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
